import { RepeatWrapping, SRGBColorSpace, TextureLoader } from 'three';
import type { Texture } from 'three';

/**
 * Texture pipeline.
 *
 * Files
 * -----
 * Texture sets live under `public/textures/<category>/<id>/`. Each set
 * has its own `meta.json` describing the file names, intended tiling
 * size, and shader-relevant scalars. A top-level `public/textures/manifest.json`
 * lists all set IDs per category so the UI / generator can enumerate
 * options without scanning the filesystem at runtime.
 *
 * Loading
 * -------
 * Sets are loaded lazily on first use and cached per-category-id. The
 * loader assigns the correct color spaces (sRGB for albedo, linear for
 * the rest), turns on repeat wrapping, and applies the meta-controlled
 * tile scale derived from a target real-world size in meters.
 *
 * Color space matters: albedo MUST be sRGB to match Three.js' lighting
 * pipeline. The other maps (normal/roughness/ao/displacement) carry
 * non-color data and stay in the default linear space.
 *
 * Hot-reload note: Vite's static asset handling means manifest.json
 * and meta.json are served as plain files; we fetch them at runtime
 * (no bundler import) so adding a new texture set requires nothing
 * but dropping the directory + editing the manifest.
 */

export interface TextureMeta {
  id: string;
  category: string;
  label: string;
  source?: string;
  license?: string;
  files: {
    albedo?: string;
    normal?: string;
    roughness?: string;
    ao?: string;
    displacement?: string;
    metalness?: string;
  };
  resolution?: string;
  /** Real-world size that one full UV tile spans (in meters). */
  tileMeters: number;
  normalScale?: number;
  displacementScale?: number;
  aoIntensity?: number;
  roughnessOffset?: number;
  tags?: string[];
}

export interface Manifest {
  version: number;
  categories: Record<string, string[]>;
}

export interface TextureSet {
  meta: TextureMeta;
  albedo?: Texture;
  normal?: Texture;
  roughness?: Texture;
  ao?: Texture;
  displacement?: Texture;
  metalness?: Texture;
}

const loader = new TextureLoader();
const setCache = new Map<string, Promise<TextureSet>>();
let manifestPromise: Promise<Manifest> | null = null;

const TEXTURES_BASE = `${import.meta.env?.BASE_URL ?? '/'}textures`.replace(/\/+/g, '/');

export async function loadManifest(): Promise<Manifest> {
  if (!manifestPromise) {
    manifestPromise = fetch(`${TEXTURES_BASE}/manifest.json`)
      .then((r) => {
        if (!r.ok) throw new Error(`manifest.json: HTTP ${r.status}`);
        return r.json() as Promise<Manifest>;
      })
      .catch((err) => {
        console.warn('[textures] manifest load failed', err);
        return { version: 1, categories: {} };
      });
  }
  return manifestPromise;
}

export async function loadTextureSet(category: string, id: string): Promise<TextureSet> {
  const key = `${category}/${id}`;
  let entry = setCache.get(key);
  if (entry) return entry;
  entry = (async () => {
    const dir = `${TEXTURES_BASE}/${category}/${id}`;
    const metaResp = await fetch(`${dir}/meta.json`);
    if (!metaResp.ok) throw new Error(`meta.json for ${key}: HTTP ${metaResp.status}`);
    const meta = (await metaResp.json()) as TextureMeta;
    const set: TextureSet = { meta };
    const load = (file?: string) =>
      file ? loader.loadAsync(`${dir}/${file}`) : Promise.resolve<Texture | undefined>(undefined);
    const [albedo, normal, roughness, ao, displacement, metalness] = await Promise.all([
      load(meta.files.albedo),
      load(meta.files.normal),
      load(meta.files.roughness),
      load(meta.files.ao),
      load(meta.files.displacement),
      load(meta.files.metalness),
    ]);
    if (albedo) {
      albedo.colorSpace = SRGBColorSpace;
      configureTextureWrap(albedo);
      set.albedo = albedo;
    }
    if (normal) {
      configureTextureWrap(normal);
      set.normal = normal;
    }
    if (roughness) {
      configureTextureWrap(roughness);
      set.roughness = roughness;
    }
    if (ao) {
      configureTextureWrap(ao);
      set.ao = ao;
    }
    if (displacement) {
      configureTextureWrap(displacement);
      set.displacement = displacement;
    }
    if (metalness) {
      configureTextureWrap(metalness);
      set.metalness = metalness;
    }
    return set;
  })();
  setCache.set(key, entry);
  return entry;
}

function configureTextureWrap(t: Texture) {
  t.wrapS = RepeatWrapping;
  t.wrapT = RepeatWrapping;
  t.anisotropy = 8;
}

/**
 * Configure a texture set's repeat for a given world-space rectangle.
 * Caller passes the rectangle's width/height in meters; we set repeat
 * to `(width / tileMeters, height / tileMeters)`. Idempotent — last
 * caller wins, so call once per material binding.
 */
export function setTextureRepeatForRect(
  set: TextureSet,
  widthMeters: number,
  heightMeters: number,
): void {
  const u = Math.max(0.01, widthMeters / set.meta.tileMeters);
  const v = Math.max(0.01, heightMeters / set.meta.tileMeters);
  for (const t of [set.albedo, set.normal, set.roughness, set.ao, set.displacement, set.metalness]) {
    if (t) t.repeat.set(u, v);
  }
}
